import { useMemo } from "react";
import styled from "styled-components";
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateTime } from "luxon";
import { formatMoney } from "../../utils/formatMoney";
import { useGetStoreStatsQuery } from "../../query/statsQuery";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";
import { Currency } from "@KassaroAB/shared/types/enums/currency";

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

interface DailyStatsLineChartProps {
  storeId: string | undefined;
}

export const DailyStatsLineChart = ({ storeId }: DailyStatsLineChartProps) => {
  const { t } = useTranslation();
  const today = DateTime.now().endOf("day");
  const from = today.minus({ days: 29 }).startOf("day").toFormat("yyyy-MM-dd");
  const to = today.toFormat("yyyy-MM-dd");

  const { data: stats, isLoading } = useGetStoreStatsQuery(
    storeId,
    "days",
    from,
    to
  );

  const chartData = useMemo(
    () =>
      (stats ?? []).map((s) => {
        const date = DateTime.fromISO(s.id);
        return {
          id: s.id,
          value: s.receipts.grossAmount.amount,
          name: date.toFormat("d MMM"),
        };
      }),
    [stats]
  );

  if (isLoading && !chartData.length) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ChartTitle>{t("overview.dailySalesLast30Days")}</ChartTitle>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={chartData}
          margin={{ top: spacing.x2, right: spacing.x2, left: 0, bottom: 0 }}
        >
          <XAxis dataKey="name" tick={{ fontSize: fontSizes.small }} />
          <YAxis
            tickFormatter={(v) => formatMoney({ amount: v, currency: Currency.SEK })}
            tick={{ fontSize: fontSizes.small }}
            width={80}
          />
          <ReferenceLine y={0} stroke={colors.border} strokeWidth={1} />
          <Tooltip
            formatter={(value: number) =>
              formatMoney({ amount: value, currency: Currency.SEK })
            }
            cursor={false}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={colors.primary}
            strokeWidth={2}
            dot={{ fill: colors.primary, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};
